import assertNever from "assert-never";
import { DayOfWeek } from "@js-joda/core";
import * as React from "react";
import * as Ons from "react-onsenui";
import {
  PlanOfCareWithAnswerAndIsOpen,
  PocChangeParams,
} from "../activities/VisitPlanOfCareActivity";
import { deepEqual } from "../deep-equal";
import { isLoading } from "../Loading";
import { POCItem, POCItemAnswer } from "../messages/PlanOfCare";
import { STRINGS } from "../strings";
import { globalize, translate } from "../translate";
import { withDefault } from "../utils/withDefault";
import {
  AppCard,
  AppCardContent,
  AppCardFooter,
  AppCardHeader,
  AppCardTitle,
} from "./Generic/AppCard";
import { scrollToViewAfterKeyboardOpen } from "./ScheduledVisitDocScreenUtils";
import { SignatureModal } from "./SignatureModal";
import classNames from "classnames";

export interface PlanOfCareComponentProps {
  planOfCare: PlanOfCareWithAnswerAndIsOpen;
  onItemChange: (item: POCItem, params: PocChangeParams) => void;
  onItemToggle: (item: POCItem) => void;
  onSubmitClick: () => void;
  onPatientSignatureEnd: (
    data: string | null,
    type: "Patient",
    unsignedReason?: string | null
  ) => void;
  onCaregiverSignatureEnd: (
    data: string | null,
    type: "Caregiver",
    unsignedReason?: string | null
  ) => void;
  caregiverSignature: string | null;
  patientSignature: string | null;
  patientUnsignedReason: string | null;
}

export interface PlanOfCareComponentState {
  showOldPlanOfCare: boolean;
  isPatientUnsigned: boolean;
  patientUnsignedReason: string | null;
}

export class PlanOfCareScreen extends React.Component<
  PlanOfCareComponentProps,
  PlanOfCareComponentState
> {
  shouldComponentUpdate(props: PlanOfCareComponentProps, state: PlanOfCareComponentState) {
    return (
      !deepEqual(props.planOfCare, this.props.planOfCare) ||
      props.caregiverSignature !== this.props.caregiverSignature ||
      props.patientSignature !== this.props.patientSignature ||
      state.showOldPlanOfCare !== this.state.showOldPlanOfCare ||
      state.isPatientUnsigned !== this.state.isPatientUnsigned ||
      state.patientUnsignedReason !== this.state.patientUnsignedReason
    );
  }
  constructor(props: PlanOfCareComponentProps) {
    super(props);

    let unsignedStatus = false;
    if (
      props.patientSignature === null &&
      props.patientUnsignedReason !== null &&
      props.patientUnsignedReason.length > 0
    ) {
      unsignedStatus = true;
    }

    this.state = {
      showOldPlanOfCare: false,
      isPatientUnsigned: unsignedStatus,
      patientUnsignedReason: unsignedStatus ? props.patientUnsignedReason : null,
    };
  }

  componentDidUpdate(_prevProps: PlanOfCareComponentProps, prevState: PlanOfCareComponentState) {
    if (prevState.showOldPlanOfCare) {
      this.forceUpdate();
    }
  }

  /* Onsen UI doesn't update on first render with some values, force rerender when the component loads */
  componentDidMount() {
    this.forceUpdate();
  }

  renderItemHeader(item: POCItem, isOpen: boolean) {
    return (
      <div
        className={"PlanOfCareComponent__headerContainer"}
        onClick={() => (this.state.showOldPlanOfCare ? null : this.props.onItemToggle(item))}
      >
        <span className="PlanOfCareComponent__headerTitle">{item.label}</span>
        <div className="PlanOfCareComponent__headerRequired">
          <div className="checkbox">
            <input
              type={"checkbox"}
              className={"PlanOfCareComponent__basicCheckBox checkbox__input"}
              disabled={this.state.showOldPlanOfCare}
              checked={isOpen}
              readOnly={true}
            />
            <span className="checkbox__checkmark" />
          </div>
          <label>{translate(STRINGS.REQUIRED)}</label>
        </div>
      </div>
    );
  }

  renderTaskBody(item: POCItem, answer: POCItemAnswer.Task | null) {
    const everyVisitSelected = () =>
      this.state.showOldPlanOfCare ? null : this.props.onItemChange(item, { everyVisit: true });
    const asNeededSelected = () =>
      this.state.showOldPlanOfCare
        ? null
        : this.props.onItemChange(item, { asRequestedByPatient: true });
    const daysAWeekSelected = () =>
      this.state.showOldPlanOfCare
        ? null
        : this.props.onItemChange(item, {
            daysAWeek: { frequencyPerWeek: null, visitsPerWeek: null },
          });

    return (
      <div className={"PlanOfCareComponent__bodyContainer"}>
        <div className={"PlanOfCareComponent__typeContainer"} onClick={everyVisitSelected}>
          <div className="checkbox">
            <input
              type={"checkbox"}
              checked={answer !== null && answer.whenToPerform.type === "EveryVisit"}
              disabled={this.state.showOldPlanOfCare}
              className={"PlanOfCareComponent__basicCheckBox checkbox__input"}
              readOnly={true}
            />
            <span className="checkbox__checkmark" />
          </div>
          {translate(STRINGS.EVERY_VISIT)}
        </div>
        <div className={"PlanOfCareComponent__typeContainer"} onClick={asNeededSelected}>
          <div className="checkbox">
            <input
              type={"checkbox"}
              checked={answer !== null && answer.whenToPerform.type === "AsRequestedByPatient"}
              disabled={this.state.showOldPlanOfCare}
              className={"PlanOfCareComponent__basicCheckBox checkbox__input"}
              readOnly={true}
            />
            <span className="checkbox__checkmark" />
          </div>
          {translate(STRINGS.AS_NEEDED)}
        </div>
        <div className={"PlanOfCareComponent__typeContainer"} onClick={daysAWeekSelected}>
          <div className="checkbox">
            <input
              type={"checkbox"}
              checked={answer !== null && answer.whenToPerform.type === "OnDaysOfWeek"}
              disabled={this.state.showOldPlanOfCare}
              className={"PlanOfCareComponent__basicCheckBox checkbox__input"}
              readOnly={true}
            />
            <span className="checkbox__checkmark" />
          </div>
          {translate(STRINGS.DAYS_A_WEEK)}
        </div>
        {answer !== null && answer.whenToPerform.type === "OnDaysOfWeek" ? (
          <div className={"PlanOfCareComponent__visitedDaysAWeek"}>
            <div>
              {translate(STRINGS.FREQUENCY)}
              <select
                value={answer.whenToPerform.frequency?.frequency ?? ""}
                onChange={(event) => {
                  const frequencyPerWeek =
                    event.target.value === "" ? null : parseInt(event.target.value, 10);
                  const visitsPerWeek =
                    answer.whenToPerform.type === "OnDaysOfWeek"
                      ? answer.whenToPerform.frequency?.visitsInAWeek ?? null
                      : null;
                  this.props.onItemChange(item, {
                    daysAWeek: {
                      frequencyPerWeek: frequencyPerWeek,
                      visitsPerWeek: visitsPerWeek,
                    },
                  });
                }}
                className={"PlanOfCareComponent__visitedDaysAWeekComponent"}
              >
                <option value="" />
                <option value="1">1</option>
                <option value="2">2</option>
                <option value="3">3</option>
                <option value="4">4</option>
                <option value="5">5</option>
                <option value="6">6</option>
                <option value="7">7</option>
              </select>
              {translate(STRINGS.VISITS_PER_WEEK)}
              <select
                value={answer.whenToPerform.frequency?.visitsInAWeek ?? ""}
                onChange={(event) => {
                  const frequencyPerWeek =
                    answer.whenToPerform.type === "OnDaysOfWeek"
                      ? answer.whenToPerform.frequency?.frequency ?? null
                      : null;
                  const visitsPerWeek =
                    event.target.value === "" ? null : parseInt(event.target.value, 10);

                  this.props.onItemChange(item, {
                    daysAWeek: {
                      frequencyPerWeek: frequencyPerWeek,
                      visitsPerWeek: visitsPerWeek,
                    },
                  });
                }}
                className={"PlanOfCareComponent__visitedDaysAWeekComponent"}
              >
                <option value="" />
                <option value="1">1</option>
                <option value="2">2</option>
                <option value="3">3</option>
                <option value="4">4</option>
                <option value="5">5</option>
                <option value="6">6</option>
                <option value="7">7</option>
              </select>
            </div>
          </div>
        ) : null}
        <div className={"PlanOfCareComponent__daysTitle"}>{translate(STRINGS.SPECIFIC_DAYS)}</div>
        <div className={"PlanOfCareComponent__daysContainer"}>
          {DayOfWeek.values().map((day) => {
            return (
              <div
                onClick={() =>
                  this.state.showOldPlanOfCare ? null : this.props.onItemChange(item, { day: day })
                }
                key={day.ordinal()}
                className={
                  "PlanOfCareComponent__dayContainer" +
                  ((
                    answer === null || answer.whenToPerform.type !== "OnDaysOfWeek"
                      ? false
                      : (answer.whenToPerform.days ?? []).indexOf(day) !== -1
                  )
                    ? " selected"
                    : "")
                }
              >
                {globalize().formatDayOfWeek(day, true).slice(0, 2)}
                {/* <div className="checkbox">
                                    <input
                                        type={"checkbox"}
                                        checked={}

                                        disabled={this.state.showOldPlanOfCare}
                                        className={"PlanOfCareComponent__basicCheckBox checkbox__input"}
                                    />
                                    <span className="checkbox__checkmark" />
                                </div> */}
              </div>
            );
          })}
        </div>
        <textarea
          rows={3}
          className={"PlanOfCareComponent__notes"}
          value={answer === null || answer.notes === null ? undefined : answer.notes}
          placeholder={"Notes..."}
          onChange={(e) =>
            this.state.showOldPlanOfCare
              ? null
              : this.props.onItemChange(item, { notes: e.target.value })
          }
          disabled={this.state.showOldPlanOfCare}
        />
      </div>
    );
  }

  renderAttributeBody(item: POCItem, answer: POCItemAnswer.Attribute | null) {
    return (
      <div className={"PlanOfCareComponent__bodyContainer"}>
        <textarea
          rows={3}
          className={"PlanOfCareComponent__notes"}
          value={answer === null || answer.notes === null ? undefined : answer.notes}
          placeholder={translate(STRINGS.NOTES)}
          onChange={(e) => this.props.onItemChange(item, { notes: e.target.value })}
        />
      </div>
    );
  }

  renderTask(item: POCItem, answer: POCItemAnswer.Task | null, isOpen: boolean) {
    return (
      <div
        className={classNames({
          PlanOfCareComponent__itemContainer: true,
          PlanOfCareScreen__requiredEmpty: isOpen && answer === null,
        })}
        key={item.code}
      >
        {this.renderItemHeader(item, isOpen)}
        <div>{isOpen ? this.renderTaskBody(item, answer) : null}</div>
      </div>
    );
  }

  renderAttribute(item: POCItem, answer: POCItemAnswer.Attribute | null, isOpen: boolean) {
    return (
      <div className={"PlanOfCareComponent__itemContainer"} key={item.code}>
        {this.renderItemHeader(item, isOpen)}
        {isOpen ? this.renderAttributeBody(item, answer) : null}
      </div>
    );
  }

  onShowOldPlanOfCareClick = () => {
    this.setState((prevState: PlanOfCareComponentState) => {
      return {
        showOldPlanOfCare: !prevState.showOldPlanOfCare,
      };
    });
  };

  onPatientSignatureEnd = (data: string | null, type: "Patient") => {
    if (!this.state.showOldPlanOfCare) {
      this.props.onPatientSignatureEnd(data, type);
    }
  };

  onCaregiverSignatureEnd = (data: string | null, type: "Caregiver") => {
    if (!this.state.showOldPlanOfCare) {
      this.props.onCaregiverSignatureEnd(data, type);
    }
  };

  render() {
    if (isLoading(this.props.planOfCare)) {
      return (
        <div className={"PlanOfCareComponent__loading"}>
          <Ons.ProgressCircular indeterminate={true} />
        </div>
      );
    }
    if (this.props.planOfCare.poc.submittedAt !== null) {
      return (
        <div>
          <div className={"PlanOfCareComponent__submitContainer"}>
            <h4>{translate(STRINGS.A_PLAN_OF_CARE_ALREADY_EXSITS_FOR_THIS_PATIENT)}</h4>
            <div>
              <Ons.Button
                className={"PlanOfCareComponent__oldPlanOfCareButton"}
                onClick={this.onShowOldPlanOfCareClick}
                modifier={"large"}
              >
                {translate(STRINGS.SHOW_OLD_PLAN_OF_CARE)}
              </Ons.Button>
            </div>
          </div>
          {this.state.showOldPlanOfCare ? this.renderPlanOfCare(this.props.planOfCare) : null}
        </div>
      );
    }

    return (
      <div>
        {this.renderPlanOfCare(this.props.planOfCare)}
        <div className={"PlanOfCareComponent__submitContainer"}>
          <Ons.Button
            onClick={this.props.onSubmitClick}
            modifier={"large"}
            disabled={
              this.props.caregiverSignature === null &&
              (this.props.patientSignature === null || this.props.patientUnsignedReason === null)
            }
          >
            {translate(STRINGS.SUBMIT)}
          </Ons.Button>
        </div>
      </div>
    );
  }

  removePatientNotSignIn() {
    this.setState({ patientUnsignedReason: null, isPatientUnsigned: false });
  }

  patientNotSignedPossibleReasons = [
    "Patient unable to sign",
    "Patient refused to sign",
    "Verbal consent given by patient/family",
    "Other",
  ];
  isEqualOrOther(savedAnswer: string | null, optionalAnswer: string): boolean {
    let boolToReturn = false;
    if (savedAnswer === optionalAnswer || this.isOther(savedAnswer, optionalAnswer)) {
      boolToReturn = true;
    }

    return boolToReturn;
  }

  isOther(savedAnswer: string | null, optionalAnswer: string): boolean {
    let boolToReturn = false;
    if (
      optionalAnswer === "Other" &&
      ((savedAnswer !== null && !this.patientNotSignedPossibleReasons.includes(savedAnswer)) ||
        savedAnswer === null)
    ) {
      boolToReturn = true;
    }

    return boolToReturn;
  }

  changeNotSigningVal = (answer: string) => {
    let answerdNotSigned = answer;
    const isOtherClicked =
      this.patientNotSignedPossibleReasons.indexOf(answerdNotSigned) ===
      this.patientNotSignedPossibleReasons.length - 1;
    if (isOtherClicked) {
      answerdNotSigned = "";
    }

    this.setState(() => {
      return {
        patientUnsignedReason: answerdNotSigned,
      };
    });

    this.props.onPatientSignatureEnd(null, "Patient", answerdNotSigned);
  };

  togglePatientIsSigning = (newIsNotSigning: boolean) => {
    if (newIsNotSigning && !this.state.showOldPlanOfCare) {
      const patientUnsignedReason = withDefault(this.state.patientUnsignedReason, "Other");
      this.props.onPatientSignatureEnd(null, "Patient", patientUnsignedReason);
    }

    this.setState({ isPatientUnsigned: newIsNotSigning });
  };

  renderPlanOfCare(planOfCare: PlanOfCareWithAnswerAndIsOpen) {
    const isPatientUnsigned = this.state.isPatientUnsigned;
    // We knew if the patient unsigned on porpos
    const patientUnsignedReason = this.state.patientUnsignedReason;

    const sectionNames: string[] = [];
    return (
      <div>
        <div>
          {planOfCare.list
            .sort((item1, item2) => {
              return +item1.item.code - +item2.item.code;
            })
            .map((itemWithAnswer, index) => {
              let section = null;
              if (sectionNames.indexOf(itemWithAnswer.item.section) === -1) {
                sectionNames.push(itemWithAnswer.item.section);
                section = (
                  <div className={"PlanOfCareComponent__sectionName"}>
                    <span>{itemWithAnswer.item.section}</span>
                  </div>
                );
              }
              switch (itemWithAnswer.item.type) {
                case "Task": {
                  return (
                    <div key={index}>
                      {section}
                      {this.renderTask(
                        itemWithAnswer.item,
                        itemWithAnswer.answer as any,
                        itemWithAnswer.isOpen
                      )}
                    </div>
                  );
                }
                case "Attribute": {
                  return (
                    <div key={index}>
                      {section}
                      {this.renderAttribute(
                        itemWithAnswer.item,
                        itemWithAnswer.answer as any,
                        itemWithAnswer.isOpen
                      )}
                    </div>
                  );
                }
                case "Predefined": {
                  return null;
                }
                default:
                  return assertNever(itemWithAnswer.item.type);
              }
            })}
          {this.props.planOfCare.planOfCareType.showPatientSignature ? (
            <AppCard style={{ margin: 16 }} className="AgencyDetailsCard">
              <AppCardHeader>
                <AppCardTitle>{translate(STRINGS.PATIENT_SIGN_HERE)}</AppCardTitle>
              </AppCardHeader>
              {/* <div className={"PlanOfCareComponent__signatureContainer"} style={this.props.planOfCare.poc.submittedAt !== null ? { pointerEvents: "none" } : {}}> */}
              <AppCardContent>
                <SignatureModal
                  onEnd={(data) => {
                    this.onPatientSignatureEnd(data, "Patient");
                    this.removePatientNotSignIn();
                  }}
                  initialData={this.props.patientSignature}
                />
              </AppCardContent>
              <AppCardFooter>
                <div>
                  <div className="ScheduledVisitDocScreen__checkBox checkbox checkbox--material">
                    <input
                      type="checkbox"
                      className="checkbox__input checkbox--material__input"
                      checked={isPatientUnsigned}
                      onChange={(event: any) => this.togglePatientIsSigning(event.target.checked)}
                    />
                    <div className="checkbox__checkmark checkbox--material__checkmark" />
                    Patient is not signing
                  </div>
                  {isPatientUnsigned ? (
                    <>
                      <div className="ScheduledVisitDocScreen_patientSignatureNotSignSection ScheduledVisitDocScreen_patientSignatureNotSignSectionShown" />
                      {this.patientNotSignedPossibleReasons.map((val, index) => {
                        const isLast = index === this.patientNotSignedPossibleReasons.length - 1;
                        return (
                          <div key={"patient_signature_" + index}>
                            <div
                              className={
                                "ScheduledVisitDocScreen__radioButton radio-button radio-button--material " +
                                (isLast ? "ScheduledVisitDocScreen__inlineBlock " : "")
                              }
                            >
                              <input
                                className="radio-button__input radio-button--material__input"
                                type="radio"
                                disabled={!isPatientUnsigned}
                                onChange={(_event) => {
                                  this.changeNotSigningVal(val);
                                }}
                                checked={this.isEqualOrOther(patientUnsignedReason, val)}
                              />
                              <div className="radio-button__checkmark radio-button--material__checkmark" />
                              {val}
                            </div>
                            {isLast && this.isOther(patientUnsignedReason, val) ? (
                              <div
                                onClick={scrollToViewAfterKeyboardOpen}
                                className="ScheduledVisitDocScreen__radioButton"
                              >
                                <input
                                  placeholder={translate(STRINGS.ENTER_TEXT_HERE)}
                                  className="ScheduledVisitDocScreen__block text-input text-input--material"
                                  onChange={(event: any) => {
                                    this.changeNotSigningVal(event.target.value);
                                  }}
                                  value={
                                    patientUnsignedReason !== null ? patientUnsignedReason : ""
                                  }
                                />
                              </div>
                            ) : null}
                          </div>
                        );
                      })}
                    </>
                  ) : null}
                </div>
              </AppCardFooter>
              {/* </div> */}
            </AppCard>
          ) : null}
          {/* <div className={"PlanOfCareComponent__signatureContainer"} style={this.props.planOfCare.poc.submittedAt !== null ? { pointerEvents: "none" } : {}}> */}
          <AppCard style={{ margin: 16 }} className="AgencyDetailsCard">
            <AppCardHeader>
              <AppCardTitle>{translate(STRINGS.RN_SIGN_HERE)}</AppCardTitle>
            </AppCardHeader>
            <AppCardContent>
              <SignatureModal
                onEnd={(data) => this.onCaregiverSignatureEnd(data, "Caregiver")}
                initialData={this.props.caregiverSignature}
              />
            </AppCardContent>
          </AppCard>
          {/* </div> */}
        </div>
      </div>
    );
  }
}
